import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import useClientInvoice from "../../hooks/useClientInvoice";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import InvoiceDetailDialog from "./InvoiceDetailDialog";

const ClientInvoicesList = ({ refreshTrigger }) => {
  const {
    invoices,
    currentInvoice,
    getInvoices,
    getInvoiceById,
    approveInvoice,
    sendInvoice,
    deleteInvoice,
  } = useClientInvoice();

  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [openInvoiceDetail, setOpenInvoiceDetail] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    filterByStatus();
  }, []);

  const filterByStatus = async () => {
    await getInvoices({ status: selectedStatus });
  };

  useEffect(() => {
    filterByStatus();
  }, [selectedStatus]);

  // Refresh when parent triggers refresh
  useEffect(() => {
    if (refreshTrigger > 0) {
      filterByStatus();
    }
  }, [refreshTrigger]);

  const handleViewInvoice = async (invoiceId) => {
    await getInvoiceById(invoiceId);
    setOpenInvoiceDetail(true);
  };

  const handleApprove = async (invoiceId) => {
    await approveInvoice(invoiceId);
    await filterByStatus();
  };

  const handleSend = async (invoiceId) => {
    await sendInvoice(invoiceId);
    await filterByStatus();
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    await deleteInvoice(deleteConfirmId);
    await filterByStatus();
    setDeleteConfirmId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "info";
      case "sent":
        return "success";
      default:
        return "default";
    }
  };

  const groupedByClient = invoices.reduce((acc, inv) => {
    const clientName = inv.client?.name || "Unknown Client";
    if (!acc[clientName]) {
      acc[clientName] = { clientId: inv.clientId, invoices: [] };
    }
    acc[clientName].invoices.push(inv);
    return acc;
  }, {});

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
    >
      {/* Status filter buttons */}
      <Box
        display="flex"
        gap={1}
      >
        {["pending", "approved", "sent"].map((status) => (
          <Button
            key={status}
            variant={selectedStatus === status ? "contained" : "outlined"}
            onClick={() => setSelectedStatus(status)}
            sx={{ textTransform: "capitalize" }}
          >
            {status}
          </Button>
        ))}
      </Box>

      {/* Invoices grouped by client */}
      {Object.entries(groupedByClient).length > 0 ? (
        Object.entries(groupedByClient).map(
          ([clientName, { clientId, invoices: clientInvoices }]) => (
            <Card key={clientId}>
              <CardContent>
                <Typography
                  variant="h6"
                  marginBottom={2}
                >
                  {clientName}
                </Typography>

                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {invoice.invoiceNumber}
                        </TableCell>
                        <TableCell>
                          {invoice.project?.name || "Unknown"}
                        </TableCell>
                        <TableCell>{invoice.invoicePeriod}</TableCell>
                        <TableCell align="right">
                          ${(invoice.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            color={getStatusColor(invoice.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={() => handleViewInvoice(invoice.id)}
                          >
                            View
                          </Button>
                          {invoice.status === "pending" && (
                            <Button
                              size="small"
                              onClick={() => handleApprove(invoice.id)}
                            >
                              Approve
                            </Button>
                          )}
                          {invoice.status === "approved" && (
                            <Button
                              size="small"
                              onClick={() => handleSend(invoice.id)}
                            >
                              Send
                            </Button>
                          )}
                          {invoice.status === "pending" && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirmId(invoice.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        )
      ) : (
        <Typography
          color="textSecondary"
          align="center"
          padding={4}
        >
          No invoices found
        </Typography>
      )}

      {/* Invoice detail dialog */}
      <InvoiceDetailDialog
        open={openInvoiceDetail}
        invoice={currentInvoice}
        onClose={() => setOpenInvoiceDetail(false)}
        onUpdate={() => {
          getInvoiceById(currentInvoice?.id);
          filterByStatus();
        }}
      />

      {/* Delete confirmation */}
      <Dialog_YesNo
        open={Boolean(deleteConfirmId)}
        title="Delete Invoice?"
        message="Are you sure you want to delete this invoice?"
        onYes={handleDelete}
        onNo={() => setDeleteConfirmId(null)}
      />
    </Box>
  );
};

export default ClientInvoicesList;
